/**
 * Query Key Factory
 * 型安全で一貫性のあるクエリキーを生成
 */

// クエリキーの型定義
export const queryKeys = {
  // 画像関連
  images: {
    all: ['images'] as const,
    lists: () => [...queryKeys.images.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.images.lists(), filters] as const,
    details: () => [...queryKeys.images.all, 'detail'] as const,
    detail: (imageId: string) => [...queryKeys.images.details(), imageId] as const,
    blob: (imageId: string) => [...queryKeys.images.all, 'blob', imageId] as const,
    metadata: (imageId: string) => [...queryKeys.images.all, 'metadata', imageId] as const,
  },

  // セット関連
  sets: {
    all: ['sets'] as const,
    lists: () => [...queryKeys.sets.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.sets.lists(), filters] as const,
    details: () => [...queryKeys.sets.all, 'detail'] as const,
    detail: (setId: string) => [...queryKeys.sets.details(), setId] as const,
    items: (setId: string) => [...queryKeys.sets.all, 'items', setId] as const,
  },

  // セットアイテム関連
  setItems: {
    all: ['setItems'] as const,
    bySet: (setId: string) => [...queryKeys.setItems.all, 'bySet', setId] as const,
    detail: (itemId: string) => [...queryKeys.setItems.all, 'detail', itemId] as const,
  },

  // ストレージ関連
  storage: {
    all: ['storage'] as const,
    usage: () => [...queryKeys.storage.all, 'usage'] as const,
    quota: () => [...queryKeys.storage.all, 'quota'] as const,
  },

  // ユーザー設定
  settings: {
    all: ['settings'] as const,
    theme: () => [...queryKeys.settings.all, 'theme'] as const,
    playback: () => [...queryKeys.settings.all, 'playback'] as const,
    capture: () => [...queryKeys.settings.all, 'capture'] as const,
  },
} as const

// ミューテーションキーの型定義
export const mutationKeys = {
  // 画像操作
  uploadImage: ['uploadImage'] as const,
  deleteImage: ['deleteImage'] as const,
  batchUploadImages: ['batchUploadImages'] as const,
  
  // セット操作
  createSet: ['createSet'] as const,
  updateSet: ['updateSet'] as const,
  deleteSet: ['deleteSet'] as const,
  
  // セットアイテム操作
  addSetItem: ['addSetItem'] as const,
  removeSetItem: ['removeSetItem'] as const,
  reorderSetItems: ['reorderSetItems'] as const,
  
  // 設定操作
  updateSettings: ['updateSettings'] as const,
  resetSettings: ['resetSettings'] as const,
} as const

// クエリキーのヘルパー関数
export function createDynamicQueryKey(
  base: readonly string[],
  params: Record<string, unknown>
): readonly unknown[] {
  return [...base, params] as const
}

// 無効化パターンのヘルパー
export const invalidationPatterns = {
  // 画像がアップロードされたら画像リストを無効化
  afterImageUpload: () => [queryKeys.images.lists()],
  
  // セットが更新されたら関連するクエリを無効化
  afterSetUpdate: (setId: string) => [
    queryKeys.sets.detail(setId),
    queryKeys.setItems.bySet(setId),
  ],
  
  // セットアイテムが変更されたら関連するクエリを無効化
  afterSetItemChange: (setId: string) => [
    queryKeys.setItems.bySet(setId),
    queryKeys.sets.detail(setId),
  ],
  
  // 全データのリフレッシュ
  refreshAll: () => [
    queryKeys.images.all,
    queryKeys.sets.all,
    queryKeys.setItems.all,
  ],
}